import { instanceOfErrorListResponse, Service } from '@genial/services';
import type { ReportingEnumsReportingFilePeriodEnum } from '@genial/services/dist/models';
import { ErrorListResponse } from '@genial/services/src';
import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { FiltersToApplyProps } from '../../hooks/useFilterTransformer';
import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { useToastCreator } from '../../hooks/useToastCreator';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../master/selector';

import {
  reportGeneratorAdditionalParametersState,
  reportGeneratorReferenceDateState,
  reportGeneratorRequestAdvisorIdState,
  reportGeneratorRequestPartnerIdState,
} from './atom';
import { findReportGeneratorDocumentType, isFeeSettlementDocumentType } from './categories';
import {
  defaultDownloadErrorMessage,
  defaultErrorMessage,
  defaultRetrySuccessMessage,
  defaultSuccessMessage,
} from './constants';
import { normalizeReportGeneratorListItem } from './normalize';
import { isReferenceDatePeriod } from './periods';
import {
  reportGeneratorCategoriesSelector,
  reportGeneratorDocumentTypeSelector,
  reportGeneratorPeriodSelector,
} from './selector';
import { ReportGeneratorListItemNormalized } from './types';

type ReportGeneratorCreatePayload = {
  documentType: string;
  periodSelected?: ReportingEnumsReportingFilePeriodEnum;
  referenceDate?: Date;
  additionalParameters?: Record<string, string>;
};

export const useReportGeneratorActions = () => {
  const { showToast, showToastFromError } = useToastCreator('report-generator-actions');
  const selectedPartner = useRecoilValue(partnerIdToApisSelector);
  const selectedAdvisor = useRecoilValue(advisorIdToApisSelector);
  const requestPartnerId = useRecoilValue(reportGeneratorRequestPartnerIdState);
  const requestAdvisorId = useRecoilValue(reportGeneratorRequestAdvisorIdState);

  const [randomId, setRandomId] = useState(1);
  const [page, setPage] = useState(1);
  const [filtersToApply, setFiltersToApply] = useState<FiltersToApplyProps>({});

  const {
    data: categories,
    loading: categoriesLoading,
    error: categoriesError,
    refresher: refreshCategories,
  } = useRecoilQuery(reportGeneratorCategoriesSelector);

  const setDocumentType = useSetRecoilState(reportGeneratorDocumentTypeSelector);
  const setPeriod = useSetRecoilState(reportGeneratorPeriodSelector);
  const setReferenceDate = useSetRecoilState(reportGeneratorReferenceDateState);
  const setAdditionalParameters = useSetRecoilState(reportGeneratorAdditionalParametersState);
  const setRequestPartnerId = useSetRecoilState(reportGeneratorRequestPartnerIdState);
  const setRequestAdvisorId = useSetRecoilState(reportGeneratorRequestAdvisorIdState);

  const clearRequestFormState = () => {
    setDocumentType(undefined);
    setPeriod(undefined);
    setReferenceDate(undefined);
    setAdditionalParameters({});
    setRequestPartnerId(undefined);
    setRequestAdvisorId(undefined);
  };

  const updateDocumentType = (value: string) => {
    setDocumentType(value);
    setReferenceDate(undefined);
    setAdditionalParameters({});
    setPage(1);

    const selectedDocumentType = findReportGeneratorDocumentType(categories ?? [], value);
    if (selectedDocumentType?.periodOptions.length === 1) {
      setPeriod(
        selectedDocumentType.periodOptions[0].value as ReportingEnumsReportingFilePeriodEnum
      );
      return;
    }

    setPeriod(undefined);
  };

  const updatePeriod = (value: ReportingEnumsReportingFilePeriodEnum) => {
    setPeriod(value);
    if (!isReferenceDatePeriod(value)) {
      setReferenceDate(undefined);
    }
    setPage(1);
  };

  const updateReferenceDate = (value: Date | undefined) => {
    setReferenceDate(value);
  };

  const updateAdditionalParameter = (name: string, value: string) => {
    setAdditionalParameters((current) => {
      const next = { ...current };
      if (!value.trim()) {
        delete next[name];
        return next;
      }
      next[name] = value;
      return next;
    });
  };

  const requestNextPage = () => {
    setPage((prev) => prev + 1);
  };

  const resetPage = () => {
    setRandomId((prev) => prev + 1);
    setPage(1);
  };

  const buildCreateRequestFields = ({
    documentType,
    periodSelected,
    referenceDate,
    additionalParameters,
  }: ReportGeneratorCreatePayload) => {
    const sanitizedAdditionalParameters =
      additionalParameters && Object.keys(additionalParameters).length
        ? Object.fromEntries(
            Object.entries(additionalParameters).filter(([, value]) => Boolean(value?.trim()))
          )
        : undefined;

    const documentTypeId = parseInt(documentType, 10);
    const isFeeSettlement = isFeeSettlementDocumentType(documentTypeId);

    return {
      advisorId: isFeeSettlement ? requestAdvisorId ?? 0 : selectedAdvisor,
      partnerId: isFeeSettlement ? requestPartnerId : (selectedPartner as number),
      documentType: documentTypeId,
      ...(periodSelected ? { reportingPeriod: periodSelected } : {}),
      ...(isReferenceDatePeriod(periodSelected) && referenceDate ? { referenceDate } : {}),
      additionalParameters: sanitizedAdditionalParameters,
    };
  };

  const checkRecentSameRequest = async (
    payload: ReportGeneratorCreatePayload
  ): Promise<ReportGeneratorListItemNormalized | undefined> => {
    try {
      const requestFields = buildCreateRequestFields(payload);
      const { data } = await Service.ReportingServices.V1.getReportingFileRecentRequestCheck({
        screenName: 'ReportGenerator',
        advisorId: requestFields.advisorId,
        partnerId: requestFields.partnerId,
        documentType: requestFields.documentType,
        reportingPeriod: requestFields.reportingPeriod,
        referenceDate: requestFields.referenceDate ?? undefined,
        additionalParameters: requestFields.additionalParameters as
          | { [key: string]: string }
          | undefined,
      });

      if (data.hasRecentRequest) {
        return normalizeReportGeneratorListItem(data.reportingFile, categories ?? []);
      }

      return undefined;
    } catch {
      return undefined;
    }
  };

  const postRequestNewReport = async (payload: ReportGeneratorCreatePayload) => {
    return Service.ReportingServices.V1.postReportingFile({
      screenName: 'ReportGenerator',
      reportingFileCreateRequest: buildCreateRequestFields(payload),
    })
      .then(() => {
        showToast(...defaultSuccessMessage);
        clearRequestFormState();
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error as object)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }
        showToast(...defaultErrorMessage);
      });
  };

  const getReportingFileAndDownload = async (report: ReportGeneratorListItemNormalized) => {
    return Service.ReportingServices.V1.getReportingFileByDownloadReportingId({
      screenName: 'ReportGenerator',
      reportingId: report.id,
    })
      .then((response) => {
        const signedGetUrl = response.data?.preSignedUrl;
        if (!signedGetUrl) {
          throw new Error('Url invalida');
        }
        window.open(signedGetUrl, '_blank');
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error)) {
          (error as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        showToast(...defaultDownloadErrorMessage);
      });
  };

  const retryReportingFile = async (report: ReportGeneratorListItemNormalized) => {
    return Service.ReportingServices.V1.patchReportingFileByReportingIdRetry({
      screenName: 'ReportGenerator',
      reportingId: report.id,
    })
      .then(() => {
        showToast(...defaultRetrySuccessMessage);
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error)) {
          (error as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        showToast(...defaultErrorMessage);
      });
  };

  return {
    page,
    randomId,
    categories: categories ?? [],
    categoriesLoading,
    categoriesError,
    refreshCategories,
    filtersToApply,
    setFiltersToApply,
    updateDocumentType,
    updatePeriod,
    updateReferenceDate,
    updateAdditionalParameter,
    requestNextPage,
    resetPage,
    postRequestNewReport,
    checkRecentSameRequest,
    getReportingFileAndDownload,
    retryReportingFile,
  };
};
